import { AppInjector, SettingsService } from 'army-builder-shared';
import { Subscription, distinctUntilChanged, map } from 'rxjs';

import { FWWDataLibrary } from '../fww-data-library';
import { Armour, BattleUnit, Equipment, Item, Skill, StatKey, Unit, UnitAI, UnitCardType, UnitTemplate } from '../models';
import { isDamagedPowerArmour } from '../../battle/service/functions/powerArmour';

import { itemModifiesArmor, itemSetsArmor } from './armour-functions';
import { FWWSettingsService } from 'fallout/src/app/settings/settings.service';

export const getItemCost = (unit: Unit, item: Item) => {
    if (item.overrideCost !== undefined) {
        return item.overrideCost;
    }
    if (unit.isRPG) {
        // TODO: RPG Item Costs
        // let customFn = CUSTOM_RPG_COSTS[item.itemType]?.[item.id];

        // if (customFn) {
        //     return customFn(unit, item);
        // }
        return item.xp || 0;
    }
    return item.cost;
};

export const getStat = (unit: Unit, statKey: StatKey, mode: UnitCardType = 'includeItemModifiers'): number => {
    const stat = unit.unitTemplate.stats[statKey];
    if (stat < 0) {
        // If a stat is less than 0 it should not be modified (and will be displayed as "X")
        return stat;
    }
    const modifier = mode === 'base' ? 0 : getStatModifier(unit, statKey);
    return (stat || 0) + modifier;
};

export const getSpecialIcons = (unit: Unit) => {
    const flatten = (arr1, arr2) => [...arr1, ...arr2];
    return [
        ...(unit.unitTemplate.specialIcons || []),
        ...[...unit.derived.perks, ...unit.derived.equipment]
            .filter((x) => x.mod && x.mod[0] && x.mod[0].specialIcons)
            .map((x) => x.mod[0].specialIcons)
            .reduce(flatten, [])
    ];
};

export const getSkill = (unitLike: Unit | BattleUnit, skillName: string, mode: UnitCardType = 'includeItemModifiers'): number => {
    let unit = unitLike as Unit;
    if ((unitLike as BattleUnit).unit) {
        unit = (unitLike as BattleUnit).unit;
    }

    if (!unit.unitTemplate.skills || !unit.unitTemplate.stats) {
        return 0;
    }

    const skill = unit.unitTemplate.skills[skillName];
    if (!skill) {
        return 0;
    }

    let value: number = getStat(unit, skill.stat as StatKey);

    if (mode === 'base') {
        return value || 0;
    }

    value += getSkillModifier(unit, skillName);
    if (skillName === 'Health' && unit.isRPG && isHeroic(unit)) {
        value--; // Heroic doesn't add 1 health in the RPG
        // N.B. We have to calculate this before we check if we want to include modifiers
        // because in the RPG we want to calculate the XP cost before any equipment is
        // added on
    }

    // Add modifiers
    if (skillName === 'Health') {
        value = value - (unit.derived.status['damage'] || 0) - (unit.derived.status['rads'] || 0);

        if (value < 0) {
            value = 0;
        }
    }

    return value || 0;
};

export const hasPerk = (unit: Unit, perkKey: string) => {
    return !!unit.options.perks.find((x) => x.key === perkKey);
};

export const getSkillColour = (unit: Unit, skillName: string): string => {
    const ranges = ['Orange', 'Yellow', 'Red', 'Green', 'Blue', 'Black'];
    const baseSkillColour = unit.unitTemplate.skills[skillName].colour;
    if (!baseSkillColour) {
        throw new Error('Skill has no range attached: ' + skillName);
    }
    const totalModValue = getItemsThatModifySkill(unit, skillName)
        .filter((x) => x.skills && x.skills[skillName] && x.skills[skillName].color)
        .map((x) => x.skills[skillName].color)
        .reduce((totalMod, mod) => totalMod + mod, 0);
    let newColourIndex = ranges.findIndex((r) => r.toUpperCase() === baseSkillColour?.toUpperCase()) + totalModValue;
    if (newColourIndex < 0) {
        newColourIndex = 0;
    }
    if (newColourIndex > ranges.length - 1) {
        newColourIndex = ranges.length - 1;
    }
    return ranges[newColourIndex];
};

const armourCache = {};
let settingsService: FWWSettingsService;
let useLegacyArmourSub: Subscription;
let useLegacyArmour = false;
export const getArmour = (unit: Unit, key: string, mode: UnitCardType = 'includeItemModifiers') => {
    const cache = armourCache[unit.id];
    // if (cache?.unit === unit && cache?.[key] !== undefined) {
    //     return cache[key];
    // }

    if (!settingsService) {
        settingsService = AppInjector.get(FWWSettingsService);
        useLegacyArmourSub = settingsService.fwwSettings$
            .pipe(
                map((s) => !!s.use_legacy_armour),
                distinctUntilChanged()
            )
            .subscribe((x) => {
                useLegacyArmour = !!x;
            });
    }

    const hardArmourKey = key.endsWith('_hard') ? key : key + '_hard';
    const softArmourKey = key.endsWith('_hard') ? key.replace('_hard', '') : key;
    const noArmour: Armour = {
        physical: 0,
        physical_hard: 0,
        energy: 0,
        energy_hard: 0,
        radiation: 0,
        radiation_hard: 0
    };

    const baseUnitArmour =
        useLegacyArmour && unit.unitTemplate.oldData?.armour ? unit.unitTemplate.oldData?.armour : unit.unitTemplate.armour;

    if (baseUnitArmour?.[softArmourKey] === -1) {
        return -1;
    }

    const equippedItems = [...unit.derived.equipment.filter((e) => e.equipped)];
    const powerArmour = equippedItems.find((e) => e.type === 'Power Armor')?.mod[0].armour?.replace;
    const itemAndPerksMod =
        mode === 'base'
            ? 0
            : [
                  ...equippedItems.filter(itemModifiesArmor),
                  ...unit.derived.perks.filter((p) => p.equipped !== false).filter(itemModifiesArmor),
                  unit.derived.leaderCard?.mod?.[0]?.armour?.mod ? unit.derived.leaderCard : undefined
              ]
                  .filter((x) => !!x)
                  .reduce((total, item) => total + (item.mod[0].armour.mod[softArmourKey] || 0), 0);
    let boostMod = mode === 'base' ? 0 : key.endsWith('_hard') && unit.derived.status.armor_boost > 0 ? 1 : 0;

    // let x: boolean = 1; // See the comment below
    // (unitModifiers || []) // TODO: Seems clunky passing this around - can we save the derived stats to the state?  Then all this should just work
    const effectModifiers =
        mode === 'base'
            ? 0
            : unit.derived.modifiers
                  .filter((mod) => mod.effects?.[0]?.armour?.mod)
                  .map((mod) => mod.effects[0].armour)
                  .reduce((agg, x) => agg + x.mod[softArmourKey] || 0, 0);
    const baseArmour = {
        ...noArmour,
        ...(equippedItems.find((e) => itemSetsArmor(e) && e.type !== 'Power Armor')?.mod[0].armour.replace || baseUnitArmour)
    };
    baseArmour[softArmourKey] += itemAndPerksMod + effectModifiers;

    let armorValue = baseArmour[key];
    if (powerArmour) {
        // We need to work out the higher value between Power Armour and regular armour,
        // but taking into account any hard armour as well, plus modifiers that apply to
        // regular armour but not power armour.

        const totalBase = (baseArmour[softArmourKey] || 0) + (baseArmour[hardArmourKey] || 0) + boostMod;
        const totalPA = (powerArmour[softArmourKey] || 0) + (powerArmour[hardArmourKey] || 0);

        // Now that we've accounted for the boost tokens in our total, reset it to 0
        // if we're getting the soft value (it should only be added to the value for
        // strong armour)
        if (key === softArmourKey) {
            boostMod = 0;
        }

        if (totalBase > totalPA) {
            armorValue = baseArmour[key] + boostMod;
        } else if (totalPA > totalBase) {
            armorValue = powerArmour[key];
        } else if ((baseArmour[hardArmourKey] || 0) > (powerArmour[hardArmourKey] || 0)) {
            // Total values are equal, so we should prefer hard armour over soft
            armorValue = (baseArmour[key] || 0) + boostMod;
        } else if ((powerArmour[hardArmourKey] || 0) > (baseArmour[hardArmourKey] || 0)) {
            // Either PA hard armour is higher or both armour items are equal
            armorValue = powerArmour[key] || 0;
        }
        return armorValue;
    } else {
        if (key === softArmourKey) {
            boostMod = 0;
        }
        armorValue = armorValue + boostMod;
    }

    armourCache[unit.id] = { ...(armourCache[unit.id] || {}), unit, [key]: armorValue };
    return armorValue;
};

export const getEquippableArmour = (unit: Unit, type: 'BASE' | 'MOD' | 'POWER') => {
    return unit.derived.equipment.filter((e) => {
        const mod = e.mod?.[0]?.armour;
        if (!mod) {
            return false;
        }
        if (type === 'BASE' && e.category === 'Armor (Base)') {
            return true;
        }
        if (type === 'POWER' && e.type === 'Power Armor') {
            return true;
        }
        if (type === 'MOD' && e.category === 'Armor (Modifier)') {
            return true;
        }
        return false;
    });
};

export const isUnequippedArmor = (unit: Unit, item: Equipment): boolean => {
    if (itemModifiesArmor(item) || itemSetsArmor(item) || item.type === 'Power Armor') {
        const carriedItem = unit.derived.equipment.find((e) => e.key === item.key);
        if (!carriedItem) {
            return false;
        }
        return !carriedItem.equipped;
    }
    return false;
};

export const isEquippedArmor = (item: Equipment): boolean => {
    switch (item.type) {
        case 'Power Armor':
        case 'Armor':
        case 'Clothing':
            return item.equipped;
        default:
            return false;
    }
};

export const isHeroic = (unit: Unit): boolean => {
    return !!unit.derived.perks.find((e) => e.key === 'HEROIC');
};

export const isLucky = (unit: Unit): boolean => {
    return isHeroic(unit) || getSpecialIcons(unit).includes('luck');
};

export const isModifier = (item: any, skillName: string) => {
    let modIndex = 0;
    if (isDamagedPowerArmour(item)) {
        modIndex = 1;
    }
    return item.equipped && item.mod?.[modIndex]?.skills?.[skillName];
};

export const isStatModifier = (item: any, statKey: string) => {
    let isArr = false;
    if (Array.isArray(item?.mod) && item.mod[0]) {
        isArr = true;
        if (item.mod[0].type === 'diminishing') {
            return false;
        }
    }
    return item?.mod && ((!isArr && item.mod?.stats?.[statKey]) || (isArr && item.mod?.[0]?.stats?.[statKey]));
};

export const getItemsThatModifySkill = (unit: Unit, skillName: string) => {
    const perks = [...unit.derived.perks, unit.derived.leaderCard, ...unit.derived.gifts, ...unit.derived.scars];
    const items = unit.derived.equipment;
    return [...perks, ...items]
        .filter((x) => !!x && isModifier(x, skillName))
        .map((x) => {
            if (isDamagedPowerArmour(x as Equipment)) {
                return x.mod[1];
            }
            return x.mod[0];
        });
};

export const getItemsThatModifyStat = (unit: Unit, statKey: string) => {
    const perks = [...unit.derived.perks, unit.derived.leaderCard, ...unit.derived.gifts, ...unit.derived.scars];
    const items = unit.derived.equipment;
    return [...perks, ...items]
        .filter((x) => !!x && isStatModifier(x, statKey))
        .map((x) => {
            if (isDamagedPowerArmour(x as Equipment)) {
                return x.mod[1];
            }
            return x.mod[0];
        });
};

export const getSkillModifier = (unit: Unit, skillName: string) => {
    const modifiers = getItemsThatModifySkill(unit, skillName);
    const unitSkillModifier = unit.unitTemplate.skills[skillName]?.mod || 0;
    return modifiers.reduce((total, mod) => total + (mod.skills[skillName] || 0), unitSkillModifier);
};

export const getStatModifier = (unit: Unit, statKey: string) => {
    const modifiers = getItemsThatModifyStat(unit, statKey);
    const itemModifier = modifiers.reduce((total, mod) => total + (mod.stats[statKey] || 0), 0);
    const effectModifier = (unit.derived.modifiers || []).reduce((total, mod) => {
        if (mod.effects?.[0]?.stats?.[statKey]) {
            return total + mod.effects[0].stats[statKey];
        }
        return total;
    }, 0);
    return itemModifier + effectModifier;
};

// export const includeAIEquipment = (unit: Unit): Unit => {
//     let aiEquipment = getAIEquipment(unit);
//     return {
//         ...unit,
//         equipment: [
//             ...unit.derived.defaultEquipment,
//             ...aiEquipment.filter((aie) => {
//                 if (!aie) {
//                     throw new Error('Missing AI equipment for unit: ' + unit.unitTemplate.name.en);
//                 }
//                 return !unit.unitTemplate.defaultEquipment.map(mapTo('name')).includes(aie.name);
//             })
//         ]
//     };
// };

export const getAI = (unitLike: Unit | UnitTemplate | BattleUnit): UnitAI => {
    if (!unitLike) {
        return null;
    }
    if ((unitLike as Unit).unitTemplateId) {
        // Unit
        const unit = unitLike as Unit;
        return unit.derived.ai;
    } else if ((unitLike as BattleUnit).unit) {
        // Battle Unit
        const battleUnit = unitLike as BattleUnit;
        const key = battleUnit.status.selectedNaturalBehaviorKey;
        if (key) {
            return FWWDataLibrary.behaviors[key].ai.ai;
        }
        return battleUnit.unit.derived.ai;
    } else {
        // Unit Template
        const unitTemplate = unitLike as UnitTemplate;
        return unitTemplate.ai[0];
    }
};

export const getAIEquipment = (unitLike: Unit | UnitTemplate | BattleUnit): Equipment[] => {
    const ai = getAI(unitLike);
    const aiEquipment = ai ? ai.equipment : [];
    return aiEquipment.map((key) => FWWDataLibrary.equipment[key]);
};

export const getDistinctAIs = (unitTemplate: UnitTemplate): UnitAI[] => {
    return unitTemplate?.ai.filter((ai) => ai.matrix) ?? [];
};

export const getAIByName = (unitTemplate: UnitTemplate, aiName: string): UnitAI => {
    return unitTemplate.ai.find((x) => x.name === aiName);
};

export const getSkillImg = (unit: Unit, skillName: Skill) => {
    const skillValue = getSkill(unit, skillName);
    let img;
    if (skillName === 'Battle Cry' || skillName === 'Battle Cry resist') {
        img = unit.unitTemplate.specialIcons.includes('battle_cry_not') ? 'battle_cry_not' : 'battle_cry_resist';
        if (skillName === 'Battle Cry' && skillValue > 0) {
            img = 'battle_cry';
        }
    }

    if (skillName === 'Thrown' || skillName === 'Presence' || img === 'battle_cry') {
        try {
            const colour = getSkillColour(unit, 'Battle Cry');
            if (colour) {
                img = (img || skillName).toLowerCase() + '_' + colour.toLowerCase();
            }
        } catch {}
    }
    return img;
};

export const getSkillName = (unit, skillName) => {
    if (skillName === 'Battle Cry' && !unit.skills['Battle Cry'].colour) {
        skillName = 'Battle Cry resist';
    }
    return skillName;
};
